document.addEventListener("DOMContentLoaded", function () {
  const input = document.getElementById("device-search-input");
  const resultsContainer = document.getElementById("device-search-results");
  const loadingIndicator = document.getElementById("search-loading");
  const searchIcon = document.getElementById("search-icon");
  const noResults = document.getElementById("no-results");
  const errorDisplay = document.getElementById("search-error");
  const loadMoreBtn = document.getElementById("load-more-btn");

  // Configuration constants
  const CONFIG = {
    API_ENDPOINT: "https://phoneneed-ab.vercel.app/api/shopify/metaobjects/search",
    MIN_QUERY_LENGTH: 2,
    DEBOUNCE_DELAY: 400, // Increased to 400ms
    SCROLL_THRESHOLD: 200,
    THROTTLE_DELAY: 50,
    MAX_RETRIES: 3,
    RETRY_DELAY: 1000,
    PLACEHOLDER_IMAGE: "/placeholder-device.png",
    TYPING_SPEED_THRESHOLD: 100 // If user types within 100ms, consider it "fast typing"
  };

  // State management
  const state = {
    query: "",
    previousQuery: "",
    isFetching: false,
    allDataLoaded: false,
    afterCursor: null,
    abortController: null,
    requestId: 0,
    retryCount: 0,
    lastInputTime: 0,
    typingBurst: false
  };

  // Enhanced render function with better error handling and security
  function renderResult(item) {
    if (!item?.name) {
      console.warn("Invalid item data:", item);
      return "";
    }

    const imageSrc = item.image || CONFIG.PLACEHOLDER_IMAGE;
    const deviceSlug = item.slug || "#";
    
    // Sanitize data to prevent XSS
    const sanitizedName = escapeHtml(item.name);
    const sanitizedBrand = escapeHtml(item.brand || "Unknown");
    const sanitizedSlug = escapeHtml(deviceSlug);
    
    return `
      <a href="/pages/devices/${sanitizedSlug}" class="device-result">
        <img src="${imageSrc}" alt="${sanitizedName}" class="device-thumb" loading="lazy" 
             onerror="this.src='${CONFIG.PLACEHOLDER_IMAGE}'" />
        <div class="device-info">
          <div class="device-name">${sanitizedName}</div>
          <div class="device-brand">${sanitizedBrand}</div>
        </div>
      </a>
    `;
  }

  // XSS prevention helper
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Enhanced fetch function with retry logic and better error handling
  async function fetchResults(reset = false) {
    if (!state.query.trim()) return;
    
    console.log('🚀 fetchResults called with query:', state.query, 'reset:', reset);
    
    // Don't fetch if we're already fetching or if all data is loaded (unless it's a reset)
    if (state.isFetching || (!reset && state.allDataLoaded)) {
      console.log('⏸️ Fetch prevented - isFetching:', state.isFetching, 'allDataLoaded:', state.allDataLoaded);
      return;
    }
    
    // Additional check: if query has changed since this fetch was scheduled, cancel it
    if (state.query !== input.value.trim()) {
      console.log('🚫 Query changed since fetch was scheduled, cancelling');
      return;
    }
    
    // Generate unique request ID to handle race conditions
    const currentRequestId = ++state.requestId;
    
    // Cancel previous fetch
    if (state.abortController) {
      state.abortController.abort();
    }
    state.abortController = new AbortController();
    const signal = state.abortController.signal;

    state.isFetching = true;
    showLoading(true);
    showSearchIcon(false);
    hideError();

    if (reset) {
      resetPagination();
      clearResults();
      state.retryCount = 0;
    }

    try {
      const url = buildApiUrl(state.query, reset ? null : state.afterCursor);
      console.log('📡 Making API request to:', url);
      
      const response = await fetchWithRetry(url, signal, currentRequestId);
      
      // Check if this is still the latest request
      if (currentRequestId !== state.requestId) {
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Double-check request is still current after async operation
      if (currentRequestId !== state.requestId) {
        return;
      }
      
      if (!data.success || !Array.isArray(data.data)) {
        throw new Error("Invalid response format");
      }

      handleSearchResults(data, reset);
      state.retryCount = 0; // Reset retry count on successful fetch
      
    } catch (error) {
      if (error.name !== "AbortError" && currentRequestId === state.requestId) {
        console.error("Search error:", error);
        
        // Show more specific error messages
        const errorMessage = getErrorMessage(error);
        showError(errorMessage);
        
        state.previousQuery = ""; // Allow retry on next input
      }
    } finally {
      if (currentRequestId === state.requestId) {
        state.isFetching = false;
        showLoading(false);
        showSearchIcon(true);
      }
    }
  }

  // Enhanced fetch with retry logic
  async function fetchWithRetry(url, signal, requestId) {
    for (let i = 0; i < CONFIG.MAX_RETRIES; i++) {
      try {
        const response = await fetch(url, { signal });
        
        // Check if request is still current
        if (requestId !== state.requestId) {
          throw new Error("Request cancelled");
        }
        
        return response;
      } catch (error) {
        if (error.name === "AbortError" || requestId !== state.requestId) {
          throw error;
        }
        
        if (i === CONFIG.MAX_RETRIES - 1) {
          throw error;
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, CONFIG.RETRY_DELAY));
        
        // Check if request is still current before retrying
        if (requestId !== state.requestId) {
          throw new Error("Request cancelled");
        }
      }
    }
  }

  // Better error message handling
  function getErrorMessage(error) {
    if (error.message.includes("Failed to fetch")) {
      return "Network connection error. Please check your internet connection.";
    } else if (error.message.includes("HTTP 429")) {
      return "Too many requests. Please wait a moment and try again.";
    } else if (error.message.includes("HTTP 5")) {
      return "Server error. Please try again later.";
    } else if (error.message.includes("timeout")) {
      return "Request timed out. Please try again.";
    } else {
      return "Unable to search devices. Please try again.";
    }
  }

  // Helper functions for better code organization
  function buildApiUrl(searchQuery, cursor) {
    let url = `${CONFIG.API_ENDPOINT}?q=${encodeURIComponent(searchQuery)}`;
    if (cursor) {
      url += `&after=${encodeURIComponent(cursor)}`;
    }
    return url;
  }

  function handleSearchResults(data, reset) {
    if (reset && data.data.length === 0) {
      showNoResults(true);
      return;
    }

    showNoResults(false);
    
    // Render results with better error handling
    const resultsHTML = data.data
      .map(item => renderResult(item))
      .filter(html => html) // Remove empty results
      .join("");
    
    if (resultsHTML) {
      resultsContainer.insertAdjacentHTML("beforeend", resultsHTML);
    }

    // Handle pagination
    if (data.pagination?.hasNextPage) {
      state.afterCursor = data.pagination.endCursor;
      showLoadMoreBtn(true);
    } else {
      state.allDataLoaded = true;
      showLoadMoreBtn(false);
    }
  }

  function resetPagination() {
    state.afterCursor = null;
    state.allDataLoaded = false;
  }

  function clearResults() {
    resultsContainer.innerHTML = "";
    showLoadMoreBtn(false);
  }

  function showLoading(show) {
    if (loadingIndicator) {
      loadingIndicator.style.display = show ? "block" : "none";
    }
  }
  function showSearchIcon(show) {
    if (searchIcon) {
      searchIcon.style.display = show ? "block" : "none";
    }
  }
  function showError(message) {
    if (errorDisplay) {
      if (message) {
        errorDisplay.textContent = message;
      }
      errorDisplay.style.display = "block";
    }
  }

  function hideError() {
    if (errorDisplay) {
      errorDisplay.style.display = "none";
    }
  }

  function showNoResults(show) {
    if (noResults) {
      noResults.style.display = show ? "block" : "none";
    }
  }

  function showLoadMoreBtn(show) {
    if (loadMoreBtn) {
      loadMoreBtn.style.display = show ? "block" : "none";
    }
  }

  // Create adaptive debounced search function
  let adaptiveTimeout;
  function debouncedSearchAdaptive(searchQuery, delay) {
    console.log('⏰ Adaptive debounce called, clearing previous timeout, delay:', delay + 'ms');
    clearTimeout(adaptiveTimeout);
    adaptiveTimeout = setTimeout(() => {
      console.log('⏰ Adaptive debounce timeout executed');
      performSearch(searchQuery);
    }, delay);
  }

  // Separate search execution function
  function performSearch(searchQuery) {
    console.log('🔍 Debounced search triggered with query:', searchQuery);
    
    // Double-check that current input still matches the debounced query
    const currentInput = input.value.trim();
    if (currentInput !== searchQuery) {
      console.log('🚫 Input changed during debounce, cancelling:', searchQuery, '→', currentInput);
      return;
    }
    
    if (searchQuery.length >= CONFIG.MIN_QUERY_LENGTH) {
      if (searchQuery === state.previousQuery) {
        console.log('❌ Duplicate query prevented:', searchQuery);
        return;
      }

      console.log('✅ API call will be made for query:', searchQuery);
      state.query = searchQuery;
      
      fetchResults(true); // true = reset pagination
      state.previousQuery = searchQuery;
    } else {
      console.log('⏹️ Query too short, clearing search state:', searchQuery);
      clearSearchState();
    }
  }

  // Enhanced input handler with typing speed detection
  function handleInput() {
    const newQuery = input.value.trim();
    const currentTime = Date.now();
    const timeSinceLastInput = currentTime - state.lastInputTime;
    
    // Detect if user is typing quickly (within threshold)
    const isFastTyping = timeSinceLastInput < CONFIG.TYPING_SPEED_THRESHOLD;
    
    console.log('⌨️ User input:', newQuery, 'Fast typing:', isFastTyping, 'Time since last:', timeSinceLastInput + 'ms');
    
    // Update typing burst state
    if (isFastTyping) {
      state.typingBurst = true;
      console.log('🏃 Fast typing detected - extending debounce');
    } else {
      state.typingBurst = false;
    }
    
    state.lastInputTime = currentTime;
    
    // Cancel any pending API requests when user is still typing
    if (state.abortController) {
      state.abortController.abort();
      console.log('🔄 Cancelled previous request due to new input');
    }
    
    // Reset the isFetching flag to allow new requests
    state.isFetching = false;
    
    // Use adaptive debouncing based on typing speed
    const adaptiveDelay = state.typingBurst ? CONFIG.DEBOUNCE_DELAY + 200 : CONFIG.DEBOUNCE_DELAY;
    console.log('⏰ Using adaptive delay:', adaptiveDelay + 'ms');
    
    // Always call debounced search with the current query
    debouncedSearchAdaptive(newQuery, adaptiveDelay);
  }

  function clearSearchState() {
    state.query = "";
    state.previousQuery = "";
    clearResults();
    showNoResults(false);
    resetPagination();
    hideError();
  }

  // Enhanced scroll handler with better threshold detection
  function handleScroll() {
    const { scrollTop, clientHeight, scrollHeight } = resultsContainer;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    
    // Load more when user is within SCROLL_THRESHOLD pixels from bottom
    if (distanceFromBottom <= CONFIG.SCROLL_THRESHOLD && !state.isFetching && !state.allDataLoaded) {
      fetchResults(false); // false = don't reset, use existing afterCursor
    }
  }

  // Utility functions
  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      const context = this;
      console.log('⏰ Debounce called, clearing previous timeout');
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        console.log('⏰ Debounce timeout executed');
        func.apply(context, args);
      }, wait);
    };
  }

  function throttle(func, limit) {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  // Enhanced event listeners with null checks
  if (input) {
    input.addEventListener("input", handleInput);
    
    // Clear search on Escape key
    input.addEventListener("keydown", function(e) {
      if (e.key === "Escape") {
        clearSearchState();
        input.blur();
      }
    });
  }

  if (loadMoreBtn) {
    loadMoreBtn.addEventListener("click", () => {
      fetchResults(false); // false = don't reset, use existing afterCursor
    });
  }

  if (resultsContainer) {
    resultsContainer.addEventListener("scroll", throttle(handleScroll, CONFIG.THROTTLE_DELAY));
  }

  // Cleanup on page unload
  window.addEventListener("beforeunload", () => {
    if (state.abortController) {
      state.abortController.abort();
    }
  });

  // Public API for external access (optional)
  window.DeviceSearch = {
    clearSearch: clearSearchState,
    search: (query) => {
      if (input) {
        input.value = query;
        handleInput();
      }
    }
  };
});